import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import BinaryBoxDestination from "../components/BinaryBoxDestination";
import BinaryBoxOrigin from "../components/BinaryBoxOrigin";
import { DraggableMessage } from "../components/Draggables";
import {
  BINARY_SYSTEM,
  COLOR_DARK,
  COLOR_HIGHLIGHTED,
  FONT
} from "../defaults";
import { convertToAlternate, increaseProgress } from "../helpers";
import {
  buttonCheck,
  level1BinaryInfoText,
  level1BinaryNoneDropped,
  level1BinaryNote,
  level1BinaryTask1,
  level1BinaryTask2,
  levelCorrect,
  levelIncorrect
} from "../strings";


const LevelNumber = 1;

function generateNumber(currentProgress) {
  const numbers = [24, 13, 61];
  if (currentProgress >= numbers.length) {
    return Math.ceil(Math.random() * 62) + 1; // Generate integer between 1 and 63.
  }
  return numbers[currentProgress];
}

const infoTextBoxStyle = {
  display: "flex",
  flexWrap: "wrap",
  width: "50dvh",
  height: "10.5dvh",
  alignItems: "center",
  position: "absolute",
  bottom: "0",
  marginBottom: "3dvh",
  backgroundColor: COLOR_DARK,
  filter: "drop-shadow(0.2svh 0.2svh 0.6svh rgba(0, 0, 0, 0.5))",
  borderRadius: "0.25em",
  padding: "0dvh 4dvh",
  border: "0.2dvh solid black"
};

function Level1Binary({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [number, setNumber] = useState(
    generateNumber(levelProgress[LevelNumber])
  );
  const [solution, setSolution] = useState([]);
  const [droppedCoins, setDroppedCoins] = useState([0, 0, 0, 0, 0, 0]); // How many coins of each type have been dropped so far. Used to check which coins to write 0 or 1 for, and for which to lower opacity.
  const [message, setMessage] = useState("");
  const [correct, setCorrect] = useState(false);
  


  useEffect(() => {
    let newNumber = generateNumber(levelProgress[LevelNumber]);
    setNumber(newNumber);
    setDroppedCoins([0, 0, 0, 0, 0, 0])
  }, [levelProgress]);

  useEffect(() => {
    let solutionString = convertToAlternate(BINARY_SYSTEM, number).padStart(6, 0); // Pad beginning with 0s up to length of 6.
    let solutionArray = Array.from(solutionString);
    setSolution(solutionArray); // E.g. if solution is 10100, this is [0,1,0,1,0,0]
    setDroppedCoins([0, 0, 0, 0, 0, 0]);
  }, [number]);

  /**
   * 
   * @param {*} item item[0] is index that corresponds to coin type.
   * @param {String} whichBox Either "top" or "bottom". Defines which box a coin was dropped into.
   */
  function handleDropCoin(item, whichBox) {
    const index = item[0];
    const newDroppedCoins = [...droppedCoins];
    if (whichBox === "top" && newDroppedCoins[index] === 1) { // Case: Coin was moved back into top box.
      newDroppedCoins[index] -= 1;
    } 
    else if (whichBox === "bottom" && newDroppedCoins[index] === 0) { // Case: Coin was moved into bottom box.
      newDroppedCoins[index] += 1;
    }
    setDroppedCoins(newDroppedCoins);
  }

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };
 
  const checkInput = () => {
    const isCorrect = (solution.toString() === droppedCoins.toString());
    setCorrect(isCorrect);
    if (isCorrect) {
      setMessage(levelCorrect)
    } else if (droppedCoins.toString() === "0,0,0,0,0,0") { // No coins have been dropped.
      setMessage(level1BinaryNoneDropped);
    } else {
      setMessage(levelIncorrect);
    }
    displayMessage();
  };

  const handleCloseMessage = () => {
    if (correct) {
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, BINARY_SYSTEM); // Triggers a useEffect.
    }
    setMessage("");
    setCorrect(false);
    setDisableInput(false);
  };

  return (
    <div>
      <div
        className="Welcome-label"
        style={{ fontFamily: FONT, fontSize: "3.5dvh", marginTop: "4dvh" }}
      >
        {level1BinaryTask1}
        <span style={{fontFamily: FONT, color: COLOR_HIGHLIGHTED, fontSize: "5dvh"}}>{number}</span>
        {level1BinaryTask2}
      </div>
      
      <DndProvider options={HTML5toTouch}>
        {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
        <div style={{ display: "flex", flexDirection: "column"}}>
          <div style={{display: "flex", flexDirection: "column", alignItems: "center", marginTop: "5dvh"}}>
            <div>
              <BinaryBoxOrigin
                id={"top"}
                onDropCoin={handleDropCoin}
                droppedCoins={droppedCoins}
                coinSize={9.5}
                />
            </div>
            <div style={{display: "flex", flexDirection: "row", marginLeft: "18dvh"}}> {/* marginLeft needs to be the sum of width and marginLeft of div below with the equal sign. */}
              <BinaryBoxDestination
                id={"bottom"}
                onDropCoin={handleDropCoin}
                droppedCoins={droppedCoins}
                coinSize={9.5}
              />
              <div style={{fontFamily: FONT, fontSize: "5dvh", height: "20dvh", width: "15dvh", alignContent: "center", marginLeft: "3dvh", color: "white", textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)"}}>
                =&nbsp;&nbsp;&nbsp;&nbsp;<span style={{color: COLOR_HIGHLIGHTED}}>{number}</span>
              </div>
            </div>
          </div>
        </div>
        <div style={{display: "flex", justifyContent: "center"}}>
          <div style={infoTextBoxStyle}>
            <span style={{fontFamily: FONT, fontSize: "1.7dvh", textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)"}}><span style={{fontWeight: "bold"}}>{level1BinaryNote}</span>{level1BinaryInfoText}</span>
          </div>
        </div>
        <button
          onClick={checkInput}
          className="checkAnswer-button"
          disabled={disableInput}
        >
          {buttonCheck}
        </button>
      </DndProvider>
    </div>
  );
}

export default Level1Binary;